<template>
  <!-- Callout do RF-23: só a ÚLTIMA reversão da avaliação. As anteriores ficam no
       Histórico da avaliação (sidebar de histórico) — este bloco não é uma
       timeline. Quem decide exibir é o card pai (status REVERTED + reversão
       existente); revalidado e aprovado, o pai deixa de renderizá-lo. -->
  <div
    class="w-full overflow-hidden rounded-lg border border-[#F1C21B] bg-[#FEF9E7] text-textPrimary"
    role="alert"
  >
    <button
      type="button"
      class="flex w-full items-start gap-2 px-3 py-2 text-left"
      :aria-expanded="isExpanded"
      aria-controls="post-station-reverted-callout-justification"
      data-testid="post-station-reverted-callout-header"
      @click="toggle"
    >
      <SVGIcon :path="mdiAlertCircle" class="mt-0.5 h-4 w-4 shrink-0 text-[#F1C21B]" />

      <span class="flex-1 text-sm font-bold">
        {{ CALLOUT_TITLE }}
        <span class="font-normal whitespace-nowrap">{{ formattedRevertedAt }}</span>
      </span>

      <SVGIcon
        :path="mdiChevronDown"
        class="h-5 w-5 shrink-0 transition-transform duration-200"
        :class="isExpanded ? 'rotate-180' : 'rotate-0'"
      />
    </button>

    <div class="h-px mx-4 mb-3 bg-[#F1C21B]"></div>

    <div
      id="post-station-reverted-callout-justification"
      class="overflow-hidden transition-[max-height] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
      :class="isExpanded ? 'max-h-96' : 'max-h-0'"
    >
      <div
        v-if="isExpanded"
        class="mx-3 mb-3 rounded-md bg-white px-3 py-2"
        data-testid="post-station-reverted-callout-justification"
      >
        <p class="text-sm font-bold">Justificativa da reversão</p>
        <!-- `whitespace-pre-line`: a justificativa é texto livre digitado no modal
             de reversão e pode ter quebras de linha. -->
        <p class="text-sm whitespace-pre-line">{{ justificationText }}</p>
        <p v-if="reversion.revertedBy" class="mt-1 text-xs text-[#666]">
          Revertido por {{ reversion.revertedBy.name }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { mdiAlertCircle, mdiChevronDown } from '@mdi/js'

import SVGIcon from '@/components/SVGIcon.vue'
import type { PostStationReversion } from '@/Network/Types/Responses/PostStationResponses'
import { formatDateTimeWithOffset } from '@/utils/date'

const props = defineProps<{
  reversion: PostStationReversion
  // Offset ISO do plantão (`-03:00`, `-04:00`…). Nulo em plantões antigos sem
  // fuso persistido — o helper cai no padrão de Brasília.
  timezone: string | null
}>()

// Texto do RF-23. Em constante para o prettier não quebrar a linha no template.
const CALLOUT_TITLE = 'Validação de pós-plantão revertida.'

// `justification` é nullable no contrato (coluna `note`, `text NULL`): sem
// fallback o bloco expandido apareceria vazio.
const NO_JUSTIFICATION_TEXT = 'Nenhuma justificativa foi registrada nesta reversão.'

const isExpanded = ref(true)

const toggle = () => {
  isExpanded.value = !isExpanded.value
}

// Fuso explícito no texto (`UTC-3`) porque a reversão pode ser feita por alguém
// em outro fuso que o do plantão.
const formattedRevertedAt = computed(() =>
  formatDateTimeWithOffset(props.reversion.revertedAt, props.timezone ?? '')
)

const justificationText = computed(() => props.reversion.justification || NO_JUSTIFICATION_TEXT)
</script>
